import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ListChecks, CheckCircle, AlertCircle, X, Clock } from 'lucide-react';
import useApiMethods from '../../../lib/useApiMethods';
import { cn } from '../../../utils/tailwindUtils';
import ReportFilters, { presetToRange } from './ReportFilters';


export default function FollowupCompletionReport() {
  const { fetch: apiFetch } = useApiMethods();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [byAssignee, setByAssignee] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [preset, setPreset] = useState('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        const { from, to } = presetToRange(preset, customFrom, customTo);
        const q = new URLSearchParams();
        if (from) q.set('from', from);
        if (to)   q.set('to', to);
        const qs = q.toString() ? `?${q.toString()}` : '';
        const [sResp, aResp] = await Promise.all([
          apiFetch(`engage/analytics/followup-summary/${qs}`),
          apiFetch(`engage/analytics/followup-by-assignee/${qs}`),
        ]);
        if (sResp?.status === 'fail') setError(sResp.message || 'Failed to load report');
        setSummary(sResp?.status === 'fail' ? null : sResp);
        const rows = aResp?.data?.data || aResp?.results || aResp?.data?.results || [];
        setByAssignee(Array.isArray(rows) ? rows : []);
      } catch (e) {
        setError(e?.response?.data?.message || 'Failed to load report');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset, customFrom, customTo, reloadTick]);

  const kpi = summary?.by_status || summary?.totals || {};
  const completionPct = summary?.completion_rate_pct ?? (kpi.total ? Math.round((kpi.completed || 0) / kpi.total * 100) : 0);

  return (
    <div className="p-4 max-w-[1200px] mx-auto space-y-4">
      <button onClick={() => navigate('/engage/reports')} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800">
        <ChevronLeft className="w-4 h-4" /> Back to Reports
      </button>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white shadow-sm">
          <ListChecks className="w-5 h-5" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Follow-up Completion</h1>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Cadence step completion rates by assignee</p>
        </div>
      </div>

      <ReportFilters
        preset={preset} setPreset={setPreset}
        customFrom={customFrom} setCustomFrom={setCustomFrom}
        customTo={customTo} setCustomTo={setCustomTo}
        onReload={() => setReloadTick(t => t + 1)}
      />

      {error && <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">{error}</div>}

      {loading ? <div className="p-8 text-center text-gray-500 dark:text-gray-400">Loading…</div> : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700"><div className="text-[10px] uppercase text-gray-500 font-medium">Total</div><div className="text-xl font-bold mt-1 text-gray-900 dark:text-white">{kpi.total || 0}</div></div>
            <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700"><div className="flex items-center gap-1.5"><CheckCircle className="w-3.5 h-3.5 text-green-500" /><span className="text-[10px] uppercase text-gray-500 font-medium">Completed</span></div><div className="text-xl font-bold mt-1 text-green-600 dark:text-green-400">{kpi.completed || 0}</div></div>
            <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700"><div className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-blue-500" /><span className="text-[10px] uppercase text-gray-500 font-medium">Scheduled</span></div><div className="text-xl font-bold mt-1 text-blue-600 dark:text-blue-400">{kpi.scheduled || 0}</div></div>
            <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700"><div className="flex items-center gap-1.5"><AlertCircle className="w-3.5 h-3.5 text-red-500" /><span className="text-[10px] uppercase text-gray-500 font-medium">Overdue</span></div><div className="text-xl font-bold mt-1 text-red-600 dark:text-red-400">{kpi.overdue || 0}</div></div>
            <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700"><div className="text-[10px] uppercase text-gray-500 font-medium">Completion %</div><div className="text-xl font-bold mt-1 text-indigo-600 dark:text-indigo-400">{completionPct}%</div></div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700"><h2 className="text-sm font-semibold text-gray-900 dark:text-white">By assignee</h2></div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900/40 text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                <tr>
                  <th className="px-4 py-2 text-left font-medium">Assignee</th>
                  <th className="px-4 py-2 text-right font-medium">Assigned</th>
                  <th className="px-4 py-2 text-right font-medium">Completed</th>
                  <th className="px-4 py-2 text-right font-medium">Overdue</th>
                  <th className="px-4 py-2 text-right font-medium">Completion %</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {byAssignee.length === 0 ? <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">No follow-up data.</td></tr>
                : byAssignee.map(row => {
                  const pct = row.assigned || row.total ? Math.round((row.completed || 0) / (row.assigned || row.total) * 100) : 0;
                  return (
                    <tr key={row.user_id ?? row.user_name ?? 'unassigned'} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                      <td className="px-4 py-2.5 font-medium text-gray-900 dark:text-white">{row.user_name || 'Unassigned'}</td>
                      <td className="px-4 py-2.5 text-right">{row.assigned || row.total || 0}</td>
                      <td className="px-4 py-2.5 text-right text-green-600 dark:text-green-400">{row.completed || 0}</td>
                      <td className="px-4 py-2.5 text-right text-red-500">{row.overdue || row.missed || 0}</td>
                      <td className="px-4 py-2.5 text-right"><span className={cn('inline-block px-2 py-0.5 rounded-full text-[10px] font-medium',
                        pct >= 80 ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' :
                        pct >= 50 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' :
                        'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300')}>{pct}%</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
